'use client';

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useLocale, useLocalizedPath } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

export default function CampsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const locale = useLocale();
  const localizePath = useLocalizedPath();
  const copy = getPublicStrings(locale).home.camps;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const camps = copy.cards;

  return (
    <section
      id="camps"
      ref={sectionRef}
      className="py-16 md:py-32 px-6 md:px-12 bg-white border-b border-border relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 grid-pattern opacity-50 pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div
            className={`mb-6 transition-all duration-800 ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold font-black">
              {copy.eyebrow}
            </span>
            <div className="w-12 h-[2px] bg-gold mt-2 mx-auto" />
          </div>
          <h2
            className={`text-4xl md:text-6xl text-foreground leading-tight font-heading font-bold mb-6 transition-all duration-800 delay-100 ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            {copy.title} <span className="text-gold italic">{copy.titleItalic}</span>
          </h2>
          <p
            className={`text-lg text-foreground/70 font-light transition-all duration-800 delay-200 ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            {copy.intro}{' '}
            <a
              href="https://www.squashpoint.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary underline-offset-2 hover:underline"
            >
              {copy.squashpoint}
            </a>
            .
          </p>
          <p
            className={`mt-4 rounded-lg border border-gold/40 bg-gold/5 px-4 py-3 text-sm font-medium text-foreground/85 transition-all duration-800 delay-300 ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            {copy.announcement}
          </p>
        </div>

        {/* Camps Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {camps.map((camp, index) => (
            <div
              key={camp.id}
              className={`group bg-background border border-border hover:border-gold hover:shadow-2xl transition-all duration-500 overflow-hidden ${
                isVisible ? 'fade-in-up' : 'fade-in-up-init'
              }`}
              style={{ animationDelay: `${300 + index * 150}ms` }}
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <AppImage
                  src={camp.image}
                  alt={camp.imageAlt}
                  fill
                  sizes="(max-width: 1024px) 100vw, 33vw"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent" />

                {/* Title Badge */}
                <div className="absolute top-4 left-4 bg-gold px-4 py-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-foreground">
                    {camp.subtitle}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl font-heading font-bold text-primary mb-4">{camp.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed mb-6">
                  {camp.description}
                </p>
                <a
                  href={localizePath('/#contact')}
                  aria-label={`${copy.registerAriaPrefix} ${camp.title} ${copy.registerAriaSuffix}`}
                  className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-primary group-hover:text-gold transition-colors"
                >
                  {copy.registerInterest}
                  <Icon
                    name="ArrowRightIcon"
                    size={14}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Callout Box */}
        <div
          className={`bg-white border-l-4 border-gold p-8 md:p-12 max-w-4xl mx-auto transition-all duration-800 delay-600 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          <div className="flex items-start gap-4">
            <Icon name="InformationCircleIcon" size={24} className="text-gold flex-shrink-0 mt-1" />
            <p className="text-base text-foreground/80 leading-relaxed italic">{copy.callout}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
